class CartPackUpsell extends HTMLElement {
  constructor() {
    super();
    this.upsellItem = null;
    this.selectedVariant = null;
    this.packName = null;
    this.packDetails = null;
    this.cartPackCount = 0;
    this.cartVariant = null;
    this.offeringPack = null;
    this.upsellLastChanceColors = [];
    this.upsellPreorderVariantIds = [];
    this.upsellHandle = null;
    this.isCompanionUpsell = false;
    this.init();
  }

  normalizeValue(value) {
    return String(value || "").trim().toLowerCase();
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  getProductOptions(product = this.upsellItem) {
    if (!product?.options) return [];

    return product.options.map((option, index) => ({
      position: index + 1,
      name: typeof option === "string" ? option : option?.name || `Option ${index + 1}`,
    }));
  }

  isColorOption(optionName) {
    const normalized = this.normalizeValue(optionName);
    return normalized === "color" || normalized === "colour";
  }

  hasExtendedCompanionOptions(product = this.upsellItem) {
    if (!this.isCompanionUpsell) return false;
    const nonColorOptions = this.getProductOptions(product).filter(
      (option) => !this.isColorOption(option.name),
    );

    return nonColorOptions.length > 1;
  }

  isSimpleColorSizeProduct(product = this.upsellItem) {
    const productOptions = this.getProductOptions(product);
    if (productOptions.length !== 2) return false;

    const hasColorOption = productOptions.some((option) =>
      this.isColorOption(option.name),
    );
    const hasSizeOption = productOptions.some(
      (option) => this.normalizeValue(option.name) === "size",
    );

    return hasColorOption && hasSizeOption;
  }

  shouldHideSimpleSizeOption(product = this.upsellItem, optionName = "") {
    if (!this.isSimpleColorSizeProduct(product)) return false;
    const normalizedName = this.normalizeValue(optionName);
    if (normalizedName !== "size") return false;
    return true;
  }

  shouldRenderSelectForOption(optionName) {
    return this.hasExtendedCompanionOptions() && !this.isColorOption(optionName);
  }

  getOptionValueArray(input) {
    if (Array.isArray(input)) {
      return input.map((value) => String(value || "").trim());
    }

    if (typeof input === "string") {
      return input
        .split("/")
        .map((value) => value.trim())
        .filter((value) => value.length);
    }

    if (input && typeof input === "object") {
      return this.getProductOptions().map(
        ({ position }) => String(input[position] || "").trim(),
      );
    }

    return [];
  }

  getSelectedOptionValues() {
    const selections = {};

    this.getProductOptions().forEach(({ position }) => {
      const select = this.querySelector(
        `select[data-option-position="${position}"]`,
      );
      if (select) {
        selections[position] = select.value;
        return;
      }

      const checkedInput = this.querySelector(
        `input[data-option-position="${position}"]:checked`,
      );
      if (checkedInput) {
        selections[position] = checkedInput.value;
        return;
      }

      if (this.selectedVariant?.[`option${position}`]) {
        selections[position] = this.selectedVariant[`option${position}`];
      }
    });

    return selections;
  }

  syncOptionControlsToVariant() {
    if (!this.selectedVariant) return;

    this.getProductOptions().forEach(({ position }) => {
      const value = this.selectedVariant[`option${position}`];
      if (!value) return;

      const select = this.querySelector(
        `select[data-option-position="${position}"]`,
      );
      if (select) {
        select.value = value;
      }

      const radio = this.querySelector(
        `input[data-option-position="${position}"][value="${CSS.escape(value)}"]`,
      );
      if (radio) {
        radio.checked = true;
      }
    });
  }

  updateOptionAvailability() {
    if (!this.upsellItem?.variants?.length) return;

    const selections = this.getSelectedOptionValues();
    const availableVariants = this.upsellItem.variants.filter(
      (variant) => variant.available,
    );

    this.getProductOptions().forEach(({ position }) => {
      const matchingValues = new Set();

      availableVariants.forEach((variant) => {
        const matchesOtherOptions = this.getProductOptions().every(
          ({ position: otherPosition }) => {
            if (otherPosition === position) return true;
            const selectedValue = selections[otherPosition];
            if (!selectedValue) return true;

            return (
              this.normalizeValue(variant[`option${otherPosition}`]) ===
              this.normalizeValue(selectedValue)
            );
          },
        );

        if (matchesOtherOptions) {
          matchingValues.add(this.normalizeValue(variant[`option${position}`]));
        }
      });

      this.querySelectorAll(
        `[data-option-position="${position}"][data-option-value]`,
      ).forEach((control) => {
        const value = this.normalizeValue(control.getAttribute("data-option-value"));
        const isAvailable = matchingValues.has(value);

        if (control.tagName === "OPTION") {
          control.disabled = !isAvailable;
          return;
        }

        control.disabled = !isAvailable;
        control.classList.toggle("disabled", !isAvailable);

        const label = this.querySelector(`label[for="${control.id}"]`);
        if (label) {
          label.style.display = isAvailable ? "" : "none";
        }
      });
    });
  }

  hideOOScolors() {
    this.updateOptionAvailability();
  }
  

  processVariantTitle(variantTitle) {
    if (!variantTitle) return "";
    
    const parts = variantTitle.split(" / ").map((s) => s.trim());
    
    if (parts[1] && parts[1].match(/^\d+$/)) { // if the second part is a number then suggest black sock ( Merino Travel Shorts)
      return "Black";
    }
    // If there are three options, return only the first two
    if (parts.length >= 3) {
      return parts.slice(0, 2).join(" / ");
    }

    return variantTitle;
  }

  getHiddenUpsellHandles() {
    if (typeof handlesForceHiddenFromCartUpselling !== "string") return [];

    return handlesForceHiddenFromCartUpselling
      .split(",")
      .map((handle) => handle.trim())
      .filter(Boolean);
  }

  findConfiguredUpsellRow(cartDrawer) {
    if (!cartDrawer) return null;

    const hiddenHandles = new Set(this.getHiddenUpsellHandles());
    const cartRows = Array.from(
      cartDrawer.querySelectorAll("tr.cart-item[data-product-handle]"),
    );
    const cartHandles = new Set(
      cartRows
        .map((row) => row.getAttribute("data-product-handle"))
        .filter(Boolean),
    );

    return (
      cartRows.find((row) => {
        const sourceHandle = row.getAttribute("data-product-handle");
        const upsellHandle = row.getAttribute("data-upsell-product-handle");

        if (!upsellHandle) return false;
        if (upsellHandle === sourceHandle) return false;
        if (hiddenHandles.has(upsellHandle)) return false;
        if (cartHandles.has(upsellHandle)) return false;

        return true;
      }) || null
    );
  }

  resizeShopifyImage(imageUrl, width = 340) {
    if (!imageUrl) return imageUrl;
    
    // Ensure URL has protocol
    let url = imageUrl.replace(/^\/\//, "https://");
    
    // Remove existing size parameters if any
    url = url.split("?")[0];
    
    // Add width parameter for Shopify image resizing
    return `${url}?width=${width}`;
  }

  getCurrentCartSavingsCents() {
    const cartDrawer =
      document.getElementById("CartDrawer") ||
      document.querySelector("cart-drawer");
    const savingsEl = cartDrawer
      ? cartDrawer.querySelector(".cart-total-savings")
      : document.querySelector(".cart-total-savings");

    if (!savingsEl) return 0;

    const totalSavings = parseInt(
      savingsEl.getAttribute("data-total-savings-cents"),
      10,
    );

    return Number.isFinite(totalSavings) ? totalSavings : 0;
  }

  async fetchUpsellProductMeta(handle) {
    this.upsellLastChanceColors = [];
    this.upsellPreorderVariantIds = [];
    this.upsellHandle = handle;

    try {
      const res = await fetch(
        `/products/${handle}?section_id=upsell-last-chance-meta`,
      );
      if (!res.ok) return;

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const script = doc.querySelector(
        "script[data-upsell-product-meta], script[data-upsell-last-chance]"
      );
      if (!script) return;

      const raw = (script.textContent || "").trim();
      if (!raw) return;

      let value;
      try {
        value = JSON.parse(raw);
      } catch (e) {
        value = raw;
      }

      const meta =
        value && typeof value === "object" && !Array.isArray(value)
          ? value
          : { lastChanceColors: value, preorderVariantIds: [] };
      const lastChanceValue = meta.lastChanceColors;
      const str = Array.isArray(lastChanceValue)
        ? lastChanceValue.join(",")
        : String(lastChanceValue || "");

      this.upsellLastChanceColors = str
        .split(",")
        .map((v) => v.trim().toLowerCase())
        .filter(Boolean);
      this.upsellPreorderVariantIds = Array.isArray(meta.preorderVariantIds)
        ? meta.preorderVariantIds.map((id) => String(id))
        : [];
    } catch (e) {}
  }

  async getUpsellItem() {
    this.isCompanionUpsell = false;

    // Find a cart row that has data-has-pack="true" (in the drawer DOM)
    const cartDrawer =
      document.getElementById("CartDrawer") ||
      document.querySelector("cart-drawer");
    const packRow = cartDrawer
      ? cartDrawer.querySelector("tr.cart-item[data-pack-name]")
      : null;
    const configuredUpsellRow = !packRow
      ? this.findConfiguredUpsellRow(cartDrawer)
      : null;
    this.packName = packRow ? packRow.getAttribute("data-pack-name") : null;
    this.packDetails = packRow
      ? packRow.getAttribute("data-pack-details")
      : null;
    const lastChanceRaw = packRow
      ? packRow.getAttribute("data-last-chance-colors") || ""
      : "";
    this.lastChanceColorsRaw = lastChanceRaw;
    this.lastChanceColors = lastChanceRaw
      .split(",")
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);

    let handle = "merino-wool-crew-neck-t-shirt";        
    if (!packRow) {
      if (configuredUpsellRow) {
        handle = configuredUpsellRow.getAttribute("data-upsell-product-handle");
        this.cartVariant =
          configuredUpsellRow.getAttribute("data-variant-title") || "";
        this.isCompanionUpsell = true;
      } else {
        // If no pack row found, check first cart product title for Men/Women
        const firstCartItem = cartDrawer
          ? cartDrawer.querySelector("tr.cart-item:first-child")
          : null;
        if (!firstCartItem) {
          return null;
        }
        const productTitle =
          firstCartItem.querySelector(".cart-item__name")?.textContent || "";
        if (productTitle.toLowerCase().includes("women")) {
          handle = "womens-merino-crew-neck-tee";
        } else {
          handle = "merino-wool-crew-neck-t-shirt";
        }

        this.cartVariant = this.processVariantTitle(
          firstCartItem.getAttribute("data-variant-title") || "",
        );
        if (this.cartVariant === "Black") {
          if (productTitle.toLowerCase().includes("women")) {
            handle = "womens-merino-ankle-socks";
          } else {
            handle = "all-season-ankle-socks";
          }
        }
      }
      this.offeringPack = false;
    } else {
      // extra checking for giftbox packs, keep suggesting the default socks upsell
      let expectedProductHandleToRecommend = packRow.getAttribute(
        "data-product-handle",
      );
      if (typeof handlesForceHiddenFromCartUpselling !== "undefined") {
        let hiddenHandles = this.getHiddenUpsellHandles();
        // Check if expected handle is NOT in the hiddenHandles array
        if (!hiddenHandles.includes(expectedProductHandleToRecommend)) {
          handle = expectedProductHandleToRecommend;
          this.cartVariant = packRow.getAttribute("data-variant-title") || "";
          this.offeringPack = true;
        }
      }
    }

    if (!handle) return null;
    await this.fetchUpsellProductMeta(handle);

    try {
      const resp = await fetch(`/products/${handle}.js`);
      if (resp.ok) {
        const product = await resp.json();
        return product;
      }
    } catch (e) {
      console.warn("Upsell: failed to fetch product JSON", e);
    }
    return null;
  }

  isFinalSaleSelected() {
    const color = (this.selectedVariant?.option1 || "").trim().toLowerCase();
    if (!color) return false;
    return this.upsellLastChanceColors.includes(color);
  }

  isPreorderSelected() {
    const variantId = String(this.selectedVariant?.id || "");
    if (!variantId) return false;
    return this.upsellPreorderVariantIds.includes(variantId);
  }

  onVariantChange() {
    this.selectedVariant = this.findMatchingVariant(
      this.upsellItem,
      this.getSelectedOptionValues(),
    );
    this.syncOptionControlsToVariant();
    this.updateOptionAvailability();
    this.updateUpsellUI();
    this.showUpsellWithDelay(0, { skipHide: true });
  }

  getPlusIconSVG() {
    return `<svg class="cart-item__upsell-add-icon" width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.37606 8.36424H4.42606V8.31424V4.63824H7.83206H7.88206V4.58824V3.85024V3.80024H7.83206H4.42606V0.250244V0.200244H4.37606H3.62006H3.57006V0.250244V3.80024H0.164062H0.114062V3.85024V4.58824V4.63824H0.164062H3.57006V8.31424V8.36424H3.62006H4.37606Z" fill="#1A1A1A" stroke="#1A1A1A" stroke-width="0.1"/></svg>`;
  }

    setAddButtonState(label, disabled) {
    const addBtn = this.querySelector(".cart-item__upsell-add");
    if (!addBtn) return;

    const isDisabled = !!disabled;

    addBtn.innerHTML = isDisabled
        ? `<span>${label}</span>`
        : `<span>${label}</span>${this.getPlusIconSVG()}`;

    addBtn.disabled = isDisabled;
    addBtn.classList.toggle("soldout", isDisabled);
    }

   showUpsellWithDelay(delay = 1000, { skipHide = false } = {}) {
  const upsellEl = this.querySelector(".cart-item__upsell");
  if (!upsellEl) return;

  // Only hide when we explicitly want to avoid flicker (cart re-render / initial load)
  if (!skipHide) {
    upsellEl.classList.remove("is-visible");
  }

  clearTimeout(this._showUpsellTimer);
  this._showUpsellTimer = setTimeout(() => {
    if (!this.selectedVariant) return;
    upsellEl.classList.add("is-visible");
  }, delay);
}





  updateUpsellUI() {
  if (!this.selectedVariant) return;

  const cartDrawer =
    document.getElementById("CartDrawer") || document.querySelector("cart-drawer");
  const currentCartSavings = this.getCurrentCartSavingsCents();

  // Price element (we will always control it here)
  const priceEl = this.querySelector(".cart-item__upsell-price");
  // Helper: format money and prefix C if currency is CAD
    const formatMoneyWithPrefix = (cents) => {
    let formatted = Shopify.formatMoney(cents).replace(/(\.|,)00\b/, "");

    if (Shopify.currency && Shopify.currency.active === "CAD") {
        formatted = "C" + formatted;
    }

    return formatted;
    };

  // Helper: format variant price (remove trailing .00 / ,00)
    const formatVariantPrice = () => {
    return formatMoneyWithPrefix(this.selectedVariant.price);
    };


  // Helper: set / show / hide price safely
  const setPriceText = () => {
    if (!priceEl) return;
    const saleEl = priceEl.querySelector(".cart-item__upsell-sale-price");
    const compareEl = priceEl.querySelector(".cart-item__upsell-compare-price");
    if (saleEl) {
      saleEl.textContent = formatVariantPrice();
    } else {
      priceEl.textContent = formatVariantPrice();
    }
    if (compareEl) {
      if (this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > this.selectedVariant.price) {
        compareEl.textContent = formatMoneyWithPrefix(this.selectedVariant.compare_at_price);
        compareEl.style.display = "";
      } else {
        compareEl.textContent = "";
        compareEl.style.display = "none";
      }
    }
  };
  const showPrice = () => {
    if (!priceEl) return;
    priceEl.style.display = "";
  };
  const hidePrice = () => {
    if (!priceEl) return;
    priceEl.style.display = "none";
  };

  // ===================== PACK UI =====================
  if (this.offeringPack) {
    // 1) Count how many cart items share the same pack name
    const packRows = cartDrawer
      ? cartDrawer.querySelectorAll(`tr.cart-item[data-pack-name="${this.packName}"]`)
      : null;

    this.cartPackCount = 0;
    this.cartPackLastChanceCount = 0;

    if (packRows) {
      packRows.forEach((row) => {
        const quantityInput = row.querySelector(".quantity__input");
        if (quantityInput) {
          this.cartPackCount += parseInt(quantityInput.value, 10) || 0;
          if (row.querySelector(".final-sale-text")){
            this.cartPackLastChanceCount += parseInt(quantityInput.value, 10) || 0;
          }
        }
      });
    }

    // 2) Regular price (compare_at_price if available, otherwise price)
    const regularPrice = this.selectedVariant.price / 100;
    const comparePrice =  (this.selectedVariant.compare_at_price) ? ( this.selectedVariant.compare_at_price / 100) : regularPrice;

    // 3) Read tier pricing from the first pack row (Liquid already applied currency conversion)
    const packRow = packRows && packRows.length > 0 ? packRows[0] : null;

    const tiers = [];


    // Tier 1 (mandatory)
    if (packRow) {
      const t1Threshold = packRow.getAttribute("data-tire-1-threshold");
      const t1Price = packRow.getAttribute("data-tire-1-price");
      if (t1Threshold && t1Price) {
        const t1PriceNum = parseFloat(t1Price);
        tiers.push({ 
          threshold: parseInt(t1Threshold, 10), 
          price: t1PriceNum, 
          number: 1,
          diff: comparePrice -t1PriceNum
        });
      }
    }

    // Tier 2 (optional)
    if (packRow) {
      const t2Threshold = packRow.getAttribute("data-tire-2-threshold");
      const t2Price = packRow.getAttribute("data-tire-2-price");
      if (t2Threshold && t2Price) {
        const t2PriceNum = parseFloat(t2Price);
        const t1Price = tiers.length > 0 ? tiers[tiers.length - 1].price : regularPrice;
        tiers.push({ 
          threshold: parseInt(t2Threshold, 10), 
          price: t2PriceNum, 
          number: 2,
          diff: comparePrice - t2PriceNum
        });
      }
    }

    // Tier 3 (optional)
    if (packRow) {
      const t3Threshold = packRow.getAttribute("data-tire-3-threshold");
      const t3Price = packRow.getAttribute("data-tire-3-price");
      if (t3Threshold && t3Price) {
        const t3PriceNum = parseFloat(t3Price);
        const t2Price = tiers.length > 0 ? tiers[tiers.length - 1].price : regularPrice;
        tiers.push({ 
          threshold: parseInt(t3Threshold, 10), 
          price: t3PriceNum, 
          number: 3,
          diff: comparePrice - t3PriceNum
        });
      }
    }

    // Tier 4 (optional)
    if (packRow) {
      const t4Threshold = packRow.getAttribute("data-tire-4-threshold");
      const t4Price = packRow.getAttribute("data-tire-4-price");
      if (t4Threshold && t4Price) {
        const t4PriceNum = parseFloat(t4Price);
        const t3Price = tiers.length > 0 ? tiers[tiers.length - 1].price : regularPrice;
        tiers.push({ 
          threshold: parseInt(t4Threshold, 10), 
          price: t4PriceNum, 
          number: 4,
          diff: comparePrice - t4PriceNum
        });
      }
    }


    // Find current tier index
    let currentTierIndex = 0; // -1 means below all tiers
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (this.cartPackCount >= tiers[i].threshold) {
        currentTierIndex = i;
        break;
      }
    }


    // Calculate savings after adding this upsell
    const currentQuantity = this.cartPackCount;
    const newQuantity = currentQuantity + 1;
    
    // Function to get price per item for a given quantity
    const getPriceForQuantity = (quantity) => {
      for (let i = tiers.length - 1; i >= 0; i--) {
        if (quantity >= tiers[i].threshold) {
          return tiers[i].price;
        }
      }
      return regularPrice; // Below all tiers
    };

    const currentPricePerItem = getPriceForQuantity(currentQuantity);
    const newPricePerItem = getPriceForQuantity(newQuantity);

      // Calculate total costs
      const currentTotalCost = currentPricePerItem *currentQuantity;
      let newTotalCost = newPricePerItem * newQuantity;
      let savingAfterAddingThisUpsell = (currentTotalCost - newTotalCost) * 100; // Convert to cents
      this.selectedLastChance = (this.upsellItem.price_max > this.selectedVariant.price ) ?  1 : 0;
      // if last chance then, 
      if (currentPricePerItem == regularPrice && newPricePerItem == tiers[0].price) { // adding this unlock the first tier (3 items)


          let lowTotalCost = ( this.upsellItem.price_min - ( tiers[currentTierIndex ].diff * 100) )  *(this.cartPackLastChanceCount + this.selectedLastChance);
          lowTotalCost += ( this.upsellItem.price_max - ( tiers[currentTierIndex ].diff * 100) )   *(newQuantity - this.cartPackLastChanceCount - this.selectedLastChance);
          const highTotalCost =  (this.upsellItem.price_max ) * newQuantity;
          savingAfterAddingThisUpsell = highTotalCost - lowTotalCost; 
        }else if (currentPricePerItem == newPricePerItem && newPricePerItem == tiers[currentTierIndex].price) { // adding this (4 items)
          let variantPrice = (this.upsellItem.price_max > this.selectedVariant.price ) ? this.upsellItem.price_max : this.selectedVariant.price;
          savingAfterAddingThisUpsell = variantPrice - ( this.selectedVariant.price - ( tiers[currentTierIndex ].diff * 100) ) ; // Already in cents
        }else if (currentPricePerItem != newPricePerItem ) { // adding this unlock the second tier (5 items)


          let lowTotalCost = ( this.upsellItem.price_min - ( tiers[currentTierIndex+1 ].diff * 100) )  *(this.cartPackLastChanceCount + this.selectedLastChance);
          lowTotalCost += ( this.upsellItem.price_max - ( tiers[currentTierIndex +1].diff * 100) )   *(newQuantity - this.cartPackLastChanceCount - this.selectedLastChance);
          
          let highTotalCost =   this.upsellItem.price_max  *newQuantity ;
          let medSaving = highTotalCost - lowTotalCost; 

          let medTotalCost = ( this.upsellItem.price_min - ( tiers[currentTierIndex ].diff * 100) )  *(this.cartPackLastChanceCount );
          medTotalCost += ( this.upsellItem.price_max - ( tiers[currentTierIndex ].diff * 100) )   *(currentQuantity - this.cartPackLastChanceCount);

          let medTotalCostH = (this.upsellItem.price_max )    *currentQuantity;

          savingAfterAddingThisUpsell = medSaving - (medTotalCostH -medTotalCost);
        }

      // If no next tier exists and no savings from tier change, show per-item discount
    // 5) Update title + price visibility
    if (savingAfterAddingThisUpsell <= 0) {
      // No savings message
      const titleEl = this.querySelector(".cart-item__upsell-title");
      if (titleEl) titleEl.textContent = "YOU MAY ALSO LIKE";

      // Always show price in this state
      setPriceText();
      showPrice();
    } else {  
      // Savings message
      const totalDisplayedSavings =
        currentCartSavings > 0
          ? currentCartSavings + savingAfterAddingThisUpsell
          : savingAfterAddingThisUpsell;
      const formattedSavingAmount = formatMoneyWithPrefix(totalDisplayedSavings).replace(/[.,]\d+/, "");
      const titleEl = this.querySelector(".cart-item__upsell-title");

      if (titleEl) titleEl.textContent = `Add 1 More and Save ${formattedSavingAmount}`;

     
      setPriceText();
      showPrice();

    }
  } else {
    // ===================== STATIC ITEM UI =====================
    const titleEl = this.querySelector(".cart-item__upsell-title");
    if (titleEl) {
      titleEl.textContent = this.isCompanionUpsell
        ? "COMPLETE THE LOOK"
        : "TRY A FAN FAVORITE!";
    }

    // Always show price for static item
    setPriceText();
    showPrice();
  }

  // ===================== SHARED (PACK + STATIC) UI =====================

  // Update selected options text
  const selectedEl = this.querySelector(".cart-item__upsell-selected");
  if (selectedEl) {
    const hideSelectedSummary =
      this.isCompanionUpsell && this.hasExtendedCompanionOptions();

    if (hideSelectedSummary) {
      selectedEl.textContent = "";
      selectedEl.hidden = true;
    } else {
      selectedEl.textContent = this.getProductOptions()
        .map(({ position }) => this.selectedVariant[`option${position}`])
        .filter(Boolean)
        .join(" / ");
      selectedEl.hidden = false;
    }
  }

  // Update image
  const imgEl = this.querySelector(".cart-item__upsell-image img");
  if (imgEl) {
    const variantImage = this.selectedVariant.featured_image?.src
      ? this.selectedVariant.featured_image.src.replace(/^\/\//, "https://")
      : this.upsellItem.featured_image;

    imgEl.src = this.resizeShopifyImage(variantImage);
  }

  // Update button state
  if (this.selectedVariant.available) {
    this.setAddButtonState("Add", false);
  } else {
    this.setAddButtonState("Sold Out", true);
  }

  this.syncOptionControlsToVariant();
  this.updateOptionAvailability();
}



  addToCart(_this) {
    if (!_this.selectedVariant) return;

    const cart = document.querySelector("cart-drawer");
    const form = document.querySelector(".cart-form #cart-form");

    let formData = {};

    if (cart) {
      const properties = {};

      if (_this.isFinalSaleSelected()) {
        properties._LPROP = "Final Sale";
      }

      if (_this.isPreorderSelected()) {
        properties["_Pre-order"] = "This is a pre-order item.";
      }

      formData["sections"] = cart
        .getSectionsToRender()
        .map((section) => section.id);
      formData["sections_url"] = window.location.pathname;
      formData["items"] = [
        {
          id: _this.selectedVariant.id,
          quantity: 1,
          properties,
        },
      ];
      cart.setActiveElement(document.activeElement);
    }

    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((response) => {
        cart.renderContents(response);
        cart.setActiveElement(document.activeElement);

        setTimeout(() => {
          fetchCart();
        }, 50);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  render() {
    const _this = this;

    if (!this.upsellItem) {
      return;
    }

    const options_with_values = this.buildOptionsWithValues(this.upsellItem);
    const hasMultiOptionCompanionLayout =
      this.hasExtendedCompanionOptions(this.upsellItem);
    const cardEl = this.querySelector(".cart-item__upsell-card");

    if (cardEl) {
      cardEl.classList.toggle(
        "cart-item__upsell-card--multi-option",
        hasMultiOptionCompanionLayout,
      );
    }

    // Update product info
    const nameEl = this.querySelector(".cart-item__upsell-name");
    if (nameEl) {
      nameEl.textContent = this.upsellItem.title;
    }

    const productUrl = this.upsellItem.url || `/products/${this.upsellHandle}`;

    const linkEl = this.querySelector(".cart-item__upsell-image");
    if (linkEl) {
      linkEl.href = productUrl;
    }

    const productLinkEl = this.querySelector(".cart-item__upsell-product-link");
    if (productLinkEl) {
      productLinkEl.href = productUrl;
    }

    const imgEl = this.querySelector(".cart-item__upsell-image img");
    if (imgEl) {
      const imageUrl =
        this.upsellItem.selectedVariant?.featured_image ||
        this.upsellItem.featured_image;
      imgEl.src = this.resizeShopifyImage(imageUrl);
      imgEl.alt = this.upsellItem.title;
    }

    // Populate option forms
    this.populateOptionForms(options_with_values);

    // Update UI with selected variant
    this.updateUpsellUI();

    // Attach event listeners
    this.attachEventListeners();

    const addBtn = this.querySelector(".cart-item__upsell-add");
    if (addBtn) {
      addBtn.addEventListener("click", function (e) {
        e.preventDefault();
        const btn = e.currentTarget;
        btn.disabled = true;
        btn.classList.add("disabled");
        _this.addToCart(_this);
      });
    }

    this.querySelector(".cart-item__upsell-card").classList.remove("loading");
    this.showUpsellWithDelay(1000);

  }

  populateOptionForms(options_with_values) {
    const variantRadios = this.querySelector(".upsell-form__variant-radios");
    if (!variantRadios) return;
    const hasMultiOptionCompanionLayout =
      this.hasExtendedCompanionOptions(this.upsellItem);

    // Clear existing content
    variantRadios.innerHTML = "";
    variantRadios.classList.toggle(
      "upsell-form__variant-radios--multi-option",
      hasMultiOptionCompanionLayout,
    );

    options_with_values.forEach((option) => {
      const safeOptionName = this.escapeHtml(option.name);

      if (this.isColorOption(option.name)) {
        const colorContainer = document.createElement("div");
        colorContainer.className =
          "upsell-form-option upsell-form__input upsell-form__color-swatches";
        colorContainer.innerHTML = `
          <div class="upsell-form__option-heading">
            <span class="option-heading__name">${safeOptionName}</span>
          </div>
          <div class="upsell-form__normal-swatches">
            <div class="upsell-form__option-values">
              ${option.values
                .filter(
                  (value) =>
                    !(typeof value === "string" && value.startsWith("_")),
                )
                .map(
                  (value) => {
                    const safeValue = this.escapeHtml(value);
                    const inputId = `upsell-${option.position}-${value}`
                      .toLowerCase()
                      .replace(/[^a-z0-9-]+/g, "-");

                    return `
                <input
                  type="radio"
                  id="${inputId}"
                  name="upsell-${safeOptionName}"
                  value="${safeValue}"
                  data-option-position="${option.position}"
                  data-option-value="${safeValue}"
                  ${option.selected_value === value ? "checked" : ""}
                >
                <label
                  class="color-swatch"
                  for="${inputId}">
                  <span
                    class="color-swatch__inner"
                    style="background-image: url(https://unbound-merino.myshopify.com/cdn/shop/files/color-${value
                      .toLowerCase()
                      .replace(/\s+/g, "-")
                      .replace(/[^a-z0-9-]/g, "")}.png)"
                    title="${safeValue}">
                  </span>
                </label>
              `;
                  },
                )
                .join("")}
            </div>
          </div>
        `;
        variantRadios.appendChild(colorContainer);
      } else if (this.shouldRenderSelectForOption(option.name)) {
        const selectContainer = document.createElement("div");
        selectContainer.className =
          "upsell-form-option upsell-form__input upsell-form__select";
        selectContainer.innerHTML = `
          <label class="upsell-form__select-label" for="upsell-select-${option.position}">
            ${safeOptionName}
          </label>
          <div class="upsell-form__select-wrapper">
            <select
              id="upsell-select-${option.position}"
              class="upsell-form__select-control"
              data-option-position="${option.position}"
            >
              ${option.values
                .map((value) => {
                  const safeValue = this.escapeHtml(value);
                  return `
                    <option
                      value="${safeValue}"
                      data-option-position="${option.position}"
                      data-option-value="${safeValue}"
                      ${option.selected_value === value ? "selected" : ""}
                    >
                      ${safeValue}
                    </option>
                  `;
                })
                .join("")}
            </select>
            <span class="upsell-form__select-chevron" aria-hidden="true"></span>
          </div>
        `;
        variantRadios.appendChild(selectContainer);
      } else {
        if (this.shouldHideSimpleSizeOption(this.upsellItem, option.name)) {
          return;
        }

        const buttonContainer = document.createElement("div");
        buttonContainer.className =
          "upsell-form-option upsell-form__input upsell-form__buttons";
        buttonContainer.innerHTML = `
          <div class="upsell-form__option-heading">
            <span class="option-heading__name">${safeOptionName}</span>
          </div>
          <div class="upsell-form__option-values" style="grid-template-columns: repeat(5, minmax(0, 1fr));">
            ${option.values
              .map((value) => {
                const safeValue = this.escapeHtml(value);
                const inputId = `upsell-${option.position}-${value}`
                  .toLowerCase()
                  .replace(/[^a-z0-9-]+/g, "-");

                return `
              <input
                type="radio"
                id="${inputId}"
                name="upsell-${safeOptionName}"
                value="${safeValue}"
                data-option-position="${option.position}"
                data-option-value="${safeValue}"
                ${option.selected_value === value ? "checked" : ""}
              >
              <label class="upsell-form__button-label" for="${inputId}">
                ${safeValue}
              </label>
            `;
              })
              .join("")}
          </div>
        `;
        variantRadios.appendChild(buttonContainer);
      }
    });

    const renderedMultiOptionLayout =
      hasMultiOptionCompanionLayout ||
      !!variantRadios.querySelector(".upsell-form__select");

    variantRadios.classList.toggle(
      "upsell-form__variant-radios--multi-option",
      renderedMultiOptionLayout,
    );
    this.querySelector(".cart-item__upsell-card")?.classList.toggle(
      "cart-item__upsell-card--multi-option",
      renderedMultiOptionLayout,
    );

    this.syncOptionControlsToVariant();
    this.hideOOScolors();
  }

  attachEventListeners() {
    const _this = this;

    // Remove existing listeners to prevent duplicates
    this.querySelectorAll(
      '.upsell-form__input input[type="radio"], .upsell-form__input select',
    ).forEach(
      (input) => {
        input.removeEventListener("change", _this.onVariantChange);
      },
    );

    // Attach new listeners
    this.querySelectorAll(
      '.upsell-form__input input[type="radio"], .upsell-form__input select',
    ).forEach(
      (input) => {
        input.addEventListener("change", function (e) {
          e.stopPropagation();
          _this.onVariantChange();
        });
      },
    );
  }

  async init() {
    // Only initialize when cart drawer is open or container is inside the drawer
    const isDrawerOpen = document.body.classList.contains("cart-opened");
    const isInDrawer = !!(
      this.closest("cart-drawer") ||
      document.getElementById("CartDrawer")?.contains(this)
    );
    if (!isDrawerOpen && !isInDrawer) {
      return;
    }
    // if any cart item has bundle discount, return
    setTimeout(() => {
      const cartItems = document.querySelectorAll(".cart-item");
      const hasBundleDiscount = Array.from(cartItems).some((item) => {
        const discount = item.querySelector(".discounts__discount");
        if (discount) {
          let discountText = discount.textContent
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase();
          return (
            discountText.includes("bundle") || discountText.includes(" + ")
          );
        }
        return false;
      });
      if (hasBundleDiscount) {
        this.closest(".drawer__inner").classList.add("hide-custom-upsell");
        return;
      }
      this.closest(".drawer__inner").classList.remove("hide-custom-upsell");
    }, 300);
    var upsellItemObject = await this.getUpsellItem();
    this.upsellItem = upsellItemObject ? upsellItemObject : null;
    if (this.upsellItem) {
      const initialSearch =
        this.cartVariant && this.cartVariant.length
          ? this.cartVariant
          : this.upsellItem.variants?.[0]?.title || "";
      this.selectedVariant = this.findMatchingVariant(
        this.upsellItem,
        initialSearch,
      );
    } else {
      this.selectedVariant = null;
    }

    this.render();
  }
  buildOptionsWithValues(product) {
    const options = [];

    product.options.forEach((op, index) => {
      const position = index + 1;
      const values = [
        ...new Set(
          product.variants.map((v) => v[`option${position}`]).filter(Boolean),
        ),
      ];

      // Use currently selected variant if present; otherwise fall back to first variant
      const selected_value = this.selectedVariant
        ? this.selectedVariant[`option${position}`]
        : product.variants[0][`option${position}`];

      options.push({
        position,
        name: typeof op === "string" ? op : op.name,
        values,
        selected_value,
      });
    });

    return options;
  }

  findMatchingVariant(product, searchInput) {
    const requestedValues = this.getOptionValueArray(searchInput);
    const variants = product.variants || [];

    if (!variants.length) return null;

    const matchesRequestedValues = (variant, { requireAll = false, availableOnly = false } = {}) => {
      if (availableOnly && !variant.available) return false;

      return requestedValues.every((requestedValue, index) => {
        if (!requestedValue) return !requireAll;

        return (
          this.normalizeValue(variant[`option${index + 1}`]) ===
          this.normalizeValue(requestedValue)
        );
      });
    };

    const exactAvailableMatch = variants.find((variant) =>
      matchesRequestedValues(variant, { requireAll: true, availableOnly: true }),
    );
    if (exactAvailableMatch) return exactAvailableMatch;

    const exactMatch = variants.find((variant) =>
      matchesRequestedValues(variant, { requireAll: true }),
    );
    if (exactMatch) return exactMatch;

    const partialAvailableMatch = variants.find((variant) =>
      matchesRequestedValues(variant, { availableOnly: true }),
    );
    if (partialAvailableMatch) return partialAvailableMatch;

    const firstAvailableVariant = variants.find((variant) => variant.available);
    if (firstAvailableVariant) return firstAvailableVariant;

    return variants[0];
  }


}

customElements.define("cart-pack-upsell", CartPackUpsell);

// Initialize the custom element when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  const upsellElement = document.querySelector("cart-pack-upsell");
  if (upsellElement) {
    upsellElement.init();
  }
});
